import {ComponentFactoryResolver, Directive, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewContainerRef} from '@angular/core';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {KeyboardEventUtils} from '@app/shared/util/general/keyboard-event-utils';
import {MentionConfig, MentionDirective} from 'angular-mentions';
import {MentionListComponent} from 'angular-mentions/lib/mention-list.component';
import {TicketUtils} from '../ticket-utils';
import {MessageTranslateService} from '@shared/services';

export interface CommentMentionItem {
  label: string;
  isGroup?: boolean;
  value?: User;
  isSpinner?: boolean;
}

/** Directive that extends angular mention directive and tries to contain all the workaround as much as possible.
 * Workarounds like grouping and not reopening mentions on mention character keypress. */
@Directive({
  selector: '[appCommentMention]',
  standalone: false
})
export class CommentMentionDirective extends MentionDirective implements OnChanges {
  private readonly defaultConfig: MentionConfig = {
    disableSort: true,
    labelKey: 'label',
    allowSpace: true,
    mentionFilter: this.mentionFilter.bind(this),
    mentionSelect: this.mentionSelect.bind(this),
    triggerChar: TicketUtils.MENTION_CHARACTER
  };
  private assigneeMentionItems: CommentMentionItem[];

  @Input() mentionsLoading = false;
  @Input() disableMentions = false;
  @Input() assigneesByEntityName: Map<string, User[]>;

  constructor(private elementRef: ElementRef<HTMLTextAreaElement>,
              private translateService: MessageTranslateService,
              componentResolver: ComponentFactoryResolver,
              viewContainerRef: ViewContainerRef) {
    super(elementRef, componentResolver, viewContainerRef);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.assigneesByEntityName) {
        if (this.assigneesByEntityName?.size) {
          const mentionItems: CommentMentionItem[] = [];
          this.assigneesByEntityName.forEach((assignees, entityName) => {
            mentionItems.push({ label: entityName, isGroup: true });
            assignees.forEach((assignee) => mentionItems.push({
              value: assignee,
              label: assignee.fullName
            }));
          });
          this.assigneeMentionItems = mentionItems;
        } else {
          this.assigneeMentionItems = null;
        }
      }
      if (changes.mentionsLoading && this.mentionsLoading) {
        // label won't really show up but angular-mentions filters out falsy values.
        const spinnerItem: CommentMentionItem = { isSpinner: true, label: this.translateService.instant('Loading...') };
        this.mentionConfig = {
          ...this.defaultConfig,
          items: [spinnerItem],
        };
      } else if (this.assigneeMentionItems) {
        this.mentionConfig = {
          ...this.defaultConfig,
          items: this.assigneeMentionItems,
        };
        // setting to one to highlight first selectable item
        if (this.mentionList && this.assigneeMentionItems.length) {
          this.mentionList.activeIndex = 1;
        }
      } else {
        this.mentionConfig = {
          ...this.defaultConfig,
          items: []
        };
      }
      this.updateConfig();
    }
  }

  /** intercepts `keyup` that `EscalatedTicketDetailModal` listens on to close modal. Intercepts it only when mentions is open. */
  @HostListener('keyup.escape', ['$event'])
  keyupHandler(event: KeyboardEvent): boolean | void {
    const mentionList = this.mentionList;
    if (mentionList && !mentionList.hidden) {
      super.stopSearch();
      event.stopPropagation();
      return false;
    }
  }

  /** On Android keyboard, on keydown events, the keyCode is always 229: https://stackoverflow.com/a/42552368
   * `angular-mentions` already handles this except for backspace which this override handles.
   * 
   * Checks `inputType` if its a kind of delete otherwise, let `angular-mentions` handle it.
   * List of `inputType`s that includes delete: https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes */
  inputHandler(inputEvent: InputEvent): void {
    if ((<any> this).lastKeyCode === KeyboardEventUtils.KEYCODE_BUFFERED) {
      if (inputEvent.inputType.startsWith('delete')) {
        this.keyHandler({
          keyCode: KeyboardEventUtils.KEYCODE_BACKSPACE,
          key: KeyboardEventUtils.KEY_BACKSPACE,
          inputEvent: true
        });
      } else {
        super.inputHandler(inputEvent);
      }
    }
  }

  /** on up/down, cancel/double the action if the next active is a group item.
   * on mention symbol, check char before and assume user is typing email if there is a character before
   * otherwise let MentionDirective.keyHandler handle
   * returns false to stop native action like up/down and null to let native action take place or delegate to `super.keyHandler` */
  keyHandler(event: Partial<KeyboardEvent> & {inputEvent?: boolean}): boolean | null {
    if (this.disableMentions || !this.mentionConfig.items?.length) {
      return null;
    }
    const cursorIndex = this.elementRef.nativeElement.selectionStart;
    const currentValue = this.elementRef.nativeElement.value;
    const isUp = event.key === KeyboardEventUtils.KEY_ARROW_UP;
    const isDown = event.key === KeyboardEventUtils.KEY_ARROW_DOWN;
    const mentionList = this.mentionList;
    /** when triggered by input event, the typed character is already there so to check the character before the typed character check -2 */
    const charIndexOfTypedChar = cursorIndex - (event.inputEvent ? 2 : 1);
    if (event.key === this.mentionConfig.triggerChar && currentValue?.charAt(charIndexOfTypedChar).trim()) {
      // if user typed the mention-char and the char before isn't a space/newline/empty, it must be an email. skip reopening of mention or filter.
      return null;
    } else if (mentionList && !mentionList.hidden) {
      if (event.key === KeyboardEventUtils.KEY_ENTER && (this.mentionsLoading || !this.activeItem || this.activeItem.isGroup || this.activeItem.isSpinner)) {
        return false;
      } else if (event.key === KeyboardEventUtils.KEY_ESCAPE) {
        // works with keyup to close the mention on keyup only.
        event.stopPropagation();
        return null;
      } else if (isUp || isDown) {
        const offset = isDown ? 1 : -1;
        const nextActiveIndex = mentionList.activeIndex + offset;
        const nextActiveItem: CommentMentionItem = mentionList.items[nextActiveIndex];
        if (nextActiveItem?.isGroup) {
          const nextNextActiveIndex = nextActiveIndex + offset;
          const nextNextActiveItem = mentionList.items[nextNextActiveIndex];
          if (!nextNextActiveItem) {
            return false;
          }

          // setTimeout because the `keyhandler()` calls `activateNextItem` which scrolls to active item but consecutive calls doesn't apply the 2nd scroll unless async.
          setTimeout(() => {
            if (offset < 0) {
              mentionList.activatePreviousItem();
            } else {
              mentionList.activateNextItem();
            }
          });
        }
      }
    }
    /** Since we allow space, the user might go on typing while the mention is still active when there is no match. This is a problem
     * as arrow keys will look like it's not working because angular-mentions will intercept it (returning false). this stops the mention
     * search so the arrow keys will work. 
     * 
     * stopping the search depends on the `super.keyHandler()` to update the `mentionList.items` via filtering so it needs to store keyhandler
     * return value and possibly stop search before returning the stored return value. */
    const keyHandlerReturn = super.keyHandler(event);
    if (mentionList) {
      if (!mentionList.items.length) {
        super.stopSearch();
      } else if (!mentionList.hidden && !(isUp || isDown)) {
        // when filtering, scroll to top and highlight first selectable item
        mentionList.reset();
        mentionList.activeIndex = 1;
      }
    }
    return keyHandlerReturn;
  }

  /** calls super showSearchList but checks if the active item is a group which should be skipped.
   * It uses the `mentionConfig.items` because `mentionList.items` and `mentionList.activeItem` is empty on first open. */
  showSearchList(nativeElement: HTMLInputElement): void {
    super.showSearchList(nativeElement);
    const mentionList = this.mentionList;
    if ((<CommentMentionItem[]>this.mentionConfig.items)?.[mentionList?.activeIndex]?.isGroup) {
      // setting `activeIndex` instead of calling `activateNextItem` because items is empty which means there is no next item initially
      mentionList.activeIndex++;
    }
  }

  /** angular-mention doesn't allow groupings so we mapped the goup as item but skip it from filter through
   * this custom filter but include it as result if there are items in that group. */
  private mentionFilter(searchString: string, mentionItems: CommentMentionItem[]): CommentMentionItem[] {
    // when loading, return all as if matched to show the spinner
    if (this.mentionsLoading) {
      return mentionItems;
    }
    const filteredItemsWithGroup: CommentMentionItem[] = [];
    mentionItems.forEach(mentionItem => {
      const lastItemIsGroup = filteredItemsWithGroup[filteredItemsWithGroup.length - 1]?.isGroup;
      if (mentionItem.isGroup) {
        if (lastItemIsGroup) {
          filteredItemsWithGroup.splice(filteredItemsWithGroup.length - 1, 1, mentionItem);
        } else {
          filteredItemsWithGroup.push(mentionItem);
        }
      } else if (this.assigneeMatchesQuery(mentionItem, searchString)) {
        filteredItemsWithGroup.push(mentionItem);
      }
    });
    if (filteredItemsWithGroup[filteredItemsWithGroup.length - 1]?.isGroup) {
      filteredItemsWithGroup.pop();
    }
    return filteredItemsWithGroup;
  }

  private assigneeMatchesQuery(assignee: CommentMentionItem, searchString: string): boolean {
    if (assignee.value) {
      return assignee.value.fullName.toLocaleLowerCase().includes(searchString.toLocaleLowerCase().trim())
        || assignee.value.email.toLocaleLowerCase().includes(searchString.toLocaleLowerCase().trim());
    }
    return false;
  }

  private mentionSelect(assignee: CommentMentionItem): string {
    return TicketUtils.MENTION_CHARACTER + assignee.value?.email + ' ';
  }

  /** using `<any>` because we are accessing a private property from `MentionDirective` for a workaround. */
  private get mentionList(): MentionListComponent {
    return (<any>this).searchList;
  }

  private get activeItem(): CommentMentionItem | null {
    return this.mentionList?.activeItem ?? null;
  }
}
